import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Lightbulb, Shield, Target, Award, ArrowRight, Users2, TrendingUp, Briefcase, Zap, HeartHandshake } from 'lucide-react';

const About = () => {
  const coreValues = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Constantly evolving with latest technologies to deliver cutting-edge solutions"
  },
  {
    icon: Shield,
    title: "Integrity",
    description: "Transparent and honest business practices that build lasting trust"
  },
  {
    icon: Target,
    title: "Customer Success",
    description: "Your growth is our priority - we succeed when you succeed"
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Delivering quality in every project with attention to detail"
  }];


  const milestones = [
  { year: "2018", event: "Company Founded", desc: "Started our journey in technology solutions" },
  { year: "2019", event: "First Major ERP Deployment", desc: "Successfully deployed enterprise solutions" },
  { year: "2021", event: "Expanded to Outsource Services", desc: "Launched support and BPO division" },
  { year: "2022", event: "Launched AI Solutions Division", desc: "Pioneered AI-powered business solutions" },
  { year: "2023", event: "1000+ Successful Projects", desc: "Milestone achievement in project delivery" },
  { year: "2025", event: "Pan India Operations", desc: "Expanded presence across entire India" }];


  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">About Us</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Empowering Businesses Since{' '}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">2018</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Cosmos Next Gen Tech Private Limited is a next-generation technology and workforce solutions 
              company committed to empowering businesses through innovation, automation, and skilled talent.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mt-16">
            <div>
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=800&auto=format&fit=crop"
                alt="Professional IT Team Collaboration"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover" />
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Who We Are</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <span className="font-semibold text-gray-900">Cosmos Next Gen Tech Private Limited (CNGTPL)</span> is a leading technology solutions and renewable energy company established in 2018. We specialize in delivering end-to-end digital transformation, enterprise software, AI-powered automation, <strong>solar EPC & green energy solutions</strong>, EV charging infrastructure, and comprehensive workforce solutions.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  With a presence across India, we serve diverse industries including manufacturing, healthcare, finance, retail, renewable energy, and oil & gas. Our expert team combines deep technical expertise with sustainable energy solutions and business acumen to deliver results that drive measurable impact.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  From custom software development and ERP implementations to <strong>solar installations, EV charging solutions, energy storage systems</strong>, managed IT services, and skilled manpower placement, we are your trusted technology and green energy partner committed to excellence, innovation, and long-term success.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-blue-100 hover:shadow-lg transition-shadow duration-300">
                  <Users2 className="w-10 h-10 text-blue-600 mb-3" />
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-blue-100 hover:shadow-lg transition-shadow duration-300">
                  <TrendingUp className="w-10 h-10 text-blue-600 mb-3" />
                  <div className="text-3xl font-bold text-gray-900">1000+</div>
                  <div className="text-sm text-gray-600">Projects Completed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="p-10 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-100">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                To become India's most trusted next-gen digital transformation and workforce partner, 
                driving innovation and excellence across industries through cutting-edge technology and exceptional talent.
              </p>
            </Card>

            <Card className="p-10 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-100">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                To empower businesses with innovative technology solutions, intelligent automation, reliable support services, 
                and skilled professionals that accelerate growth, optimize operations, and deliver exceptional ROI.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose CNGTPL?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What makes us the preferred technology partner for businesses across India
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white border-2 border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">End-to-End Solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                From strategy and development to deployment and support—we handle your complete technology lifecycle under one roof.
              </p>
            </Card>

            <Card className="p-8 bg-white border-2 border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rapid Delivery</h3>
              <p className="text-gray-600 leading-relaxed">
                Agile methodologies and experienced teams ensure faster time-to-market without compromising on quality.
              </p>
            </Card>

            <Card className="p-8 bg-white border-2 border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <HeartHandshake className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Long-Term Partnership</h3>
              <p className="text-gray-600 leading-relaxed">
                We don't just deliver projects—we build lasting relationships with ongoing support and strategic guidance.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do and define who we are
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) =>
            <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 bg-white border-2 hover:border-blue-200">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Company Milestones */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A decade of growth, innovation, and excellence
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-600 to-cyan-500"></div>

            {milestones.map((milestone, index) =>
            <div key={index} className={`relative mb-12 ${index % 2 === 0 ? 'text-right pr-1/2' : 'text-left pl-1/2'}`}>
                <div className={`flex items-center ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12' : 'pl-12'}`}>
                    <Card className="p-6 bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 hover:shadow-lg transition-all duration-300">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{milestone.event}</h3>
                      <p className="text-gray-600">{milestone.desc}</p>
                    </Card>
                  </div>
                </div>
                {/* Timeline dot */}
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how we can help transform your business
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-medium shadow-lg">
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default About;